import { Component, OnInit } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { HttpErrorResponse } from '@angular/common/http';
import { BixmlService } from 'src/app/services/bixml.service';
import { downloadFile, getStringDate } from 'src/app/common/util';
import { ResponseObject } from 'src/app/models/response-object.model';

export interface BankTransferLogResponse {
  id: number;
  sourceAccountNumber: string;
  destinationAccountNumber: string;
  sourceBankName: string;
  destinationBankName: string;
  sourceAccountType: string;
  destinationAccountType: string;
  amount: number;
  transactionStatus: 'PENDING' | 'SUCCESS' | 'FAILED' | 'TIMEOUT' | 'ACCEPTED';
  errorMessage?: string;
  sendTimestamp: string;
  description?: string;
  authorization?: string;
  createdAt: {
    year: number;
    month: string;
    monthValue: number;
    dayOfMonth: number;
    hour: number;
    minute: number;
    second: number;
  };
}



@Component({
  selector: 'app-bi-transfer-history',
  templateUrl: './bi-transfer-history.component.html',
  styleUrls: ['./bi-transfer-history.component.scss']
})
export class BiTransferHistoryComponent {
  // Fechas de filtro
  startDate: string | null = null;
  endDate: string | null = null;
  accountFilter: string = "";

  // Datos
  pagination = {
    total: [] as BankTransferLogResponse[], // todos los registros traídos
    filter: [] as BankTransferLogResponse[], // registros visibles en la página actual
    pageSize: 10,
    startElement: 0,
    lastElement: 0,
    pageIndex: 0
  };

  loading: boolean = false;
  loadingDownload: { [id: number]: boolean } = {};

  constructor(
    private bixmlService: BixmlService,
    private toastr: ToastrService
  ) {
    const today = new Date();
    this.endDate = getStringDate(today);
    this.startDate = getStringDate(today);
  }

  ngOnInit(): void {
    this.getTransfers();
  }

  getTransfers(): void {
    this.loading = true;

    this.bixmlService.getHistory({
      startDate: this.startDate,
      endDate: this.endDate
    }).subscribe({
      next: (res: any) => {
        this.pagination.total = res.data;
        this.updateFilter();
      },
      error: (err: HttpErrorResponse) => {
        this.toastr.error("Error al obtener transferencias", "Error");
        console.error(err);
      }
    });
  }

  updateFilter() {
    let data = [...this.pagination.total]; // o la lista completa de transfers cargados

    // Filtro por número de cuenta
    if (this.accountFilter && this.accountFilter.trim() !== "") {
      const accountSearch = this.accountFilter.trim().toLowerCase();
      data = data.filter(t =>
        t.sourceAccountNumber?.toLowerCase().includes(accountSearch) ||
        t.destinationAccountNumber?.toLowerCase().includes(accountSearch)
      );
    }

    this.pagination.total = data;
    this.resetPagination();
  }

  resetPagination() {
    this.pagination.filter = this.pagination.total.slice(0, this.pagination.pageSize);
    this.pagination.pageIndex = 0;
    this.pagination.lastElement = this.pagination.filter.length;
    this.pagination.startElement = 0;
  }

  convertToString(dateStr: string | undefined): string {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('es-GT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  changePage(pageIndex: number) {
    this.pagination.pageIndex = pageIndex;
    const start = pageIndex * this.pagination.pageSize;
    const end = start + this.pagination.pageSize;
    this.pagination.filter = this.pagination.total.slice(start, end);
    this.pagination.startElement = start;
    this.pagination.lastElement = this.pagination.filter.length;
  }


  formatCreatedAt(createdAt: any): string {
    if (!createdAt) return '-';

    const day = createdAt.dayOfMonth.toString().padStart(2, '0');
    const month = createdAt.monthValue.toString().padStart(2, '0');
    const year = createdAt.year;

    const hour = createdAt.hour.toString().padStart(2, '0');
    const minute = createdAt.minute.toString().padStart(2, '0');
    const second = createdAt.second.toString().padStart(2, '0');

    return `${day}/${month}/${year} ${hour}:${minute}:${second}`;
  }

  downloadAcceptancePdf(transfer: BankTransferLogResponse): void {
    if (!transfer.authorization) {
      return;
    }

    this.loadingDownload[transfer.id] = true;

    this.bixmlService.downloadTransferPdf(transfer.id).subscribe({
      next: (response: ResponseObject) => {
        downloadFile(
          response.data,
          'pdf',
          `Comprobante de Transferencia`
        );

        this.toastr.success('PDF descargado correctamente', 'Descarga completa');
        this.loadingDownload[transfer.id] = false;
      },
      error: (error: HttpErrorResponse) => {
        if (error.status === 400 && error.error?.message) {
          this.toastr.error(error.error.message);
        } else {
          this.toastr.error('Error al descargar el PDF de autorización', 'Error');
        }

        console.error(error);
        this.loadingDownload[transfer.id] = false;
      }
    });
  }
}
